import * as PIXI from 'pixi.js'

import { Spine as _Spine } from 'pixi-spine'

export const McSpine = _Spine || PIXI.spine.Spine

const { Loader } = PIXI
let _textures = {}
let _spines = {}
const replacedTextures = {}
const replacedSpines = {}
const replacedSpinesRawData = {}
const _spineHashes = {}
let stage
let returningSpines = []

// Recursive function looking for spine objects.
function searchAllChildren(node) {
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i]
    searchAllChildren(child)
    if (child.spineData) {
      // Store the original spine name for reference.
      if (!child.origSpineName) {
        child.origSpineName = Object.keys(_spineHashes).find(k => _spineHashes[k] === child.spineData.hash)
      }
      returningSpines.push(child)
    }
  }

  return returningSpines
}

export const convertUrlToBase64 = fileUrl => atob(fileUrl.split(',').pop())

/**
 * Update the hash for corresponding spine.
 * @param {*} spineName - Name of the spine.
 * @param {string} hash - A hash of the current skeleton.
 */
export function setSpineHash(spineName, hash) {
  _spineHashes[spineName] = hash
}

export function storeReplacedSpine(spineName, data) {
  replacedSpinesRawData[spineName] = data
}

export function getReplacedSpineData() {
  return replacedSpinesRawData
}

/**
 * Store a reference to the current stage.
 * @param {*} stageRef - Stage object
 */
export function setStageRef(stageRef) {
  stage = stageRef
}

export function getStageRef() {
  return stage
}

export function getSpineName(hash) {
  return Object.keys(_spineHashes).find(key => _spineHashes[key] === hash)
}

/**
 * Find all spines corresponding the spine name in the current stage.
 */
export function getSpinesFromStage(spineName) {
  returningSpines = []
  const spinesInStage = searchAllChildren(stage)

  if (spineName) {
    const spineHash = _spineHashes[spineName]
    return spinesInStage.filter(s => s.skeleton.data.hash === spineHash && (s.origSpineName === spineName || !s.origSpineName))
  }

  // Return all spines if no name was specified.
  return spinesInStage
}

/**
 * Get all active textures in PIXI.
 * @returns {*} Returns an array with all currently active PIXI textures.
 */
export function getTextureList() {
  const { resources } = PIXI.Loader.shared
  const ret = {}

  Object.keys(resources).forEach((key) => {
    if (key.endsWith('_image') || key.indexOf('_atlas') > -1) {
      return
    }
    if (resources[key].xhrType === 'json' && resources[key].textures) {
      const { textures } = resources[key]
      Object.keys(textures).forEach(textureKey => { ret[textureKey] = textures[textureKey] })
    } else if (resources[key].texture) {
      ret[key] = resources[key].texture
    }
  })

  _textures = { ...ret, ..._textures }

  return _textures
}

/**
 * Get all active spine textures in PIXI.
 * @returns {*} Returns an array with all currently active PIXI spines.
 */
export function getSpineList() {
  const { resources } = Loader.shared
  const spines = {}
  const returnedSpines = {}

  Object.keys(resources).forEach((key) => {
    if (key.endsWith('_image') || key.endsWith('_atlas')) {
      return
    }

    if (resources[key].texture && key.indexOf('atlas_page') > -1) {
      const spineName = key.split('_atlas_page_')[0]
      if (!spines[spineName]) {
        spines[spineName] = []
      }
      const spineFile = resources[spineName]
      const spineResource = spineFile.children.find(c => c.name === key)

      // Check if we have replaced spines for this spine.
      if (replacedSpines[spineName]) {
        // We can have multiple PNGs for a single spine, we only need to return the replaced images once.
        if (returnedSpines[spineName]) {
          return
        }
        returnedSpines[spineName] = true
        Object.keys(replacedSpines[spineName]).forEach(k => {
          spines[spineName].push(replacedSpines[spineName][k])
        })
      } else if (spineResource) {
        spines[spineName].push(resources[key].texture)
      } else {
        console.log(`Spine does not use resource: ${key}`)
      }
    }
  })

  _spines = { ..._spines, ...spines }

  return _spines
}

/**
 * Replaces a in memory texture, will require a reload to take effect
 * @param name {string} Name of the texture
 * @param texture {object} The replace-texture
 */
export function replaceTexture(name, texture) {
  _textures[name] = texture
}

/**
 * Replace base for an existing spine asset.
 * @param {*} param0 - Object containing spine name, image name, index and texture.
 */
export function replaceSpineBase({ spineName, imgName, index, texture }) {
  const spineArray = _spines[spineName]
  if (!spineArray) {
    console.error('[replaceSpineBase] No spine found for ', spineName)
    return
  }

  _spines[spineName][index].textureCacheIds = [ `${spineName}_atlas_page_${imgName}`, `${index}` ]
  _spines[spineName][index].baseTexture = texture
  _spines[spineName][index]._frame = _spines[spineName][index].frame = new PIXI.Rectangle(0, 0, texture.width, texture.height)
  _spines[spineName][index].trim = null
  _spines[spineName][index].requiresUpdate = true


  if (!replacedSpines[spineName]) {
    replacedSpines[spineName] = {}
  }

  replacedSpines[spineName][imgName] = _spines[spineName][index]
}

/**
 * Removes or adds pages in spine atlas as needed.
 * @param {*} param0 - Object containing spine atlas and spine name.
 */
export function updateSpinePages({ spineAtlas, spineName }) {
  replacedSpines[spineName] = {}
  const newPages = spineAtlas.pages.filter((value, index, self) =>
    value && value.name.indexOf('.png') > -1 &&
    self.indexOf(value) === index)

  if (newPages.length < _spines[spineName].length) {
    // If new spine uses less pages (PNGs) than previously, remove excess pages.
    _spines[spineName].splice(newPages.length, _spines[spineName].length - newPages.length)
  } else if (newPages.length > _spines[spineName].length) {
    // If new spine uses more pages (PNGs) than previously, add new pages.
    const numberOfNewPages = newPages.length - _spines[spineName].length
    const refPage = _spines[spineName][0]
    for (let i = 0; i < numberOfNewPages; i++) {
      _spines[spineName].push(Object.assign(Object.create(Object.getPrototypeOf(refPage)), refPage))
    }
  }
}

/**
 * Replaces the base-texture of a in memory texture
 * @param name {string} Name of the in-memory texture
 * @param texture {object} The new basetexture
 */
export function replaceBaseTexture(name, texture) {
  _textures[name].baseTexture = texture
  _textures[name]._frame = _textures[name].frame = new PIXI.Rectangle(0, 0, texture.width, texture.height)
  _textures[name].trim = null
  _textures[name]._rotate = 0
  _textures[name].updateUvs()
  replacedTextures[name] = texture
}

/**
 * Gets the local texture with the provided name.
 * @param name {string} Name of the texture
 * @returns {image} Returns the local PIXI texture
 */
export function getLocalTexture(name) {
  if (name.indexOf('atlas_page') > -1) {
    const spineName = name.split('_atlas_page_')[0]
    return _spines[spineName].find(spine => spine.textureCacheIds[0] === name)
  }
  return _textures[name]
}

/**
 * Returns all the replaced textures in memory
 */
export function getReplacedTextures() {
  return { ...replacedTextures }
}

/**
 * Search for texture in all atlases and return texture
 * @param name {string} Name of the texture
 * @returns {*} Returns PIXI texture, or null if not found.
 */
export function findTexture(name) {
  if (_textures[name]) {
    return _textures[name]
  }

  const { resources } = PIXI.Loader.shared

  if (resources[name] && resources[name].texture) {
    _textures[name] = resources[name].texture
    return _textures[name]
  }


  const atlases = Object.values(resources).filter((resource) => resource.xhrType === 'json')

  const atlas = atlases.find((a) => {
    const { textures } = a
    return !!textures && !!textures[name]
  })

  if (!atlas) {
    console.warn(`[Textures] Could not find texture [${name}]`)
    return null
  }

  _textures[name] = atlas.textures[name]

  return atlas.textures[name]
}

/**
 * Search for texture in single or multipack atlas and return texture
 * @param atlas {string} Name of the atlas
 * @param name {string} Name of the texture
 * @returns {*} Returns PIXI texture, or false if not found.
 */
export function getTexture(atlas, name) {
  const _textureName = name

  if (_textures[_textureName]) {
    return _textures[_textureName]
  }

  const { resources } = PIXI.Loader.shared

  let i = 0
  if (resources[`${atlas}-${i}`]) {
    while (i >= 0) {
      const _atlas = resources[`${atlas}-${i}`]
      if (!_atlas) {
        i = -1
        console.warn(`[Textures] Could not find texture [${name}]`)
        return false
      }

      if (_atlas.textures[name]) {
        i = -1
        _textures[_textureName] = _atlas.textures[name]
        return _atlas.textures[name]
      }

      i += 1
    }
  } else if (resources[atlas]) {
    _textures[_textureName] = resources[atlas].textures[name]
    return resources[atlas].textures[name]
  } else {
    return false
  }
}

/**
 * Load textures into an array
 * @param symbolName
 * @param frames
 * @param atlas {string} Optional - Name of atlas where to to look for the animations
 * @returns {Array}
 */
export function loadAnimation(symbolName, frames, atlas) {
  const animationFrames = []
  for (let i = 0; i < frames; i++) {
    animationFrames[i] = getTexture(atlas || 'animations', `${symbolName}/${symbolName}${i + 1}`)
    if (!animationFrames[i]) {
      console.warn(`Animation frame ${symbolName}/${symbolName}${i + 1} not found`);
    }
  }
  return animationFrames
}
